import { ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, isBefore, isSameDay, isToday, isYesterday } from 'date-fns';
import { AlertTriangle, CalendarClock, CheckSquare, Inbox, Mail, MailOpen } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from '@/hooks/useUserRole';
import { hasFeatureAccess } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';
import { Button } from '@/components/ui/button';
import { googleWorkspace } from '@/lib/googleWorkspace';
import { isGoogleAuthUrl } from '@/lib/googleWorkspaceLogic';
import { useToast } from '@/hooks/use-toast';

interface Props { onNavigate: (view: string) => void }

interface EmailRow { id: string; subject: string | null; sender_name: string | null; received_at: string | null; escalation_flag: boolean | null; is_read: boolean | null; project_id: string | null }
interface MeetingRow { id: string; title: string | null; start_time: string | null; ai_summary: string | null; ai_action_items: Array<string | { owner?: string; action?: string; due?: string }> | null }
interface ProjectRow { id: string; name: string }

type Item =
  | { kind: 'email'; id: string; at: Date; title: string; sub: string; escalated: boolean; unread: boolean; project?: string }
  | { kind: 'meeting'; id: string; at: Date; title: string; sub: string; actions: number };

// Action items arrive either as objects or as text like "Do the thing (@Owner, due: 2026-10-09)".
function parseActionItem(a: string | { owner?: string; action?: string; due?: string }): { owner: string; action: string; due: Date | null } {
  if (typeof a !== 'string') return { owner: a.owner ?? 'Unassigned', action: a.action ?? '', due: a.due ? new Date(a.due) : null };
  const m = a.match(/^(.*?)\s*\(@([^,)]+?)(?:,\s*due:\s*([\d-]+))?\)\s*$/);
  if (!m) return { owner: 'Unassigned', action: a, due: null };
  const due = m[3] ? new Date(m[3]) : null;
  return { owner: m[2].trim(), action: m[1].trim(), due: due && !Number.isNaN(due.getTime()) ? due : null };
}

const DAYS = 14;
const since = () => new Date(Date.now() - DAYS * 86400_000).toISOString();

const dayLabel = (d: Date) => (isToday(d) ? 'Today' : isYesterday(d) ? 'Yesterday' : format(d, 'EEEE d MMM'));

const Stat = ({ label, value, tone }: { label: string; value: number; tone?: 'alert' }) => {
  const { tx } = useLocalization();
  return (
  <div className="rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
    <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{tx(label)}</p>
    <p className={cn('mt-1 font-heading text-2xl font-bold', tone === 'alert' && value > 0 && 'text-destructive')}>{value}</p>
  </div>
);
};

const Panel = ({ title, icon, action, onAction, children }: { title: string; icon: ReactNode; action?: string; onAction?: () => void; children: ReactNode }) => {
  const { tx } = useLocalization();
  return (
  <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
    <div className="mb-3 flex items-center gap-2">
      <span className="text-primary" aria-hidden="true">{icon}</span>
      <h2 className="font-heading text-base font-semibold">{tx(title)}</h2>
      {action && onAction && (
        <button type="button" onClick={onAction} className="ms-auto rounded text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{action}</button>
      )}
    </div>
    {children}
  </section>
);
};

/** Everything said in the last two weeks, in one timeline, with the follow-ups it created. */
const CommunicationHub = ({ onNavigate }: Props) => {
  const { tx, t, ago } = useLocalization();
  const { toast } = useToast();
  const { role } = useUserRole();
  const canEmail = hasFeatureAccess(role, 'inbox');
  const canMeetings = hasFeatureAccess(role, 'meetings');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['comm-hub', canEmail, canMeetings],
    staleTime: 60_000,
    queryFn: async () => {
      const [em, mt, pr] = await Promise.all([
        canEmail
          ? supabase.from('emails_sync').select('id,subject,sender_name,received_at,escalation_flag,is_read,project_id')
              .gte('received_at', since()).or('direction.eq.inbound,direction.is.null').order('received_at', { ascending: false }).limit(60)
          : Promise.resolve({ data: [] as EmailRow[], error: null }),
        canMeetings
          ? supabase.from('meetings_sync').select('id,title,start_time,ai_summary,ai_action_items')
              .gte('start_time', since()).order('start_time', { ascending: false }).limit(30)
          : Promise.resolve({ data: [] as MeetingRow[], error: null }),
        supabase.from('projects_sync').select('id,name'),
      ]);
      if (em.error) throw new Error(em.error.message);
      if (mt.error) throw new Error(mt.error.message);
      return { emails: (em.data ?? []) as EmailRow[], meetings: (mt.data ?? []) as unknown as MeetingRow[], projects: (pr.data ?? []) as ProjectRow[] };
    },
  });

  // Shares the key with the Google settings panel. Only asked once we know the
  // mailbox is empty; a failed status call just hides the prompt.
  const mailEmpty = canEmail && !isLoading && !isError && (data?.emails ?? []).length === 0;
  const { data: googleStatus } = useQuery({
    queryKey: ['google-workspace', 'status'],
    queryFn: () => googleWorkspace.status(),
    enabled: mailEmpty,
    retry: false,
  });
  const connectGoogle = async () => {
    try {
      const { url } = await googleWorkspace.authUrl();
      if (isGoogleAuthUrl(url)) window.location.assign(url);
      else toast({ title: t('Google'), description: t('Could not start the Google connection.'), variant: 'destructive' });
    } catch {
      toast({ title: t('Google'), description: t('Could not start the Google connection.'), variant: 'destructive' });
    }
  };

  const view = useMemo(() => {
    const projectName = new Map((data?.projects ?? []).map((p) => [p.id, p.name]));
    const items: Item[] = [
      ...(data?.emails ?? []).filter((e) => e.received_at).map((e): Item => ({
        kind: 'email', id: e.id, at: new Date(e.received_at as string), title: e.subject ?? '(no subject)',
        sub: e.sender_name ?? 'Unknown sender', escalated: !!e.escalation_flag, unread: e.is_read === false,
        project: e.project_id ? projectName.get(e.project_id) : undefined,
      })),
      ...(data?.meetings ?? []).filter((m) => m.start_time).map((m): Item => ({
        kind: 'meeting', id: m.id, at: new Date(m.start_time as string), title: m.title ?? 'Meeting',
        sub: m.ai_summary ?? 'No summary yet', actions: m.ai_action_items?.length ?? 0,
      })),
    ].sort((a, b) => b.at.getTime() - a.at.getTime());

    const groups: Array<{ label: string; day: Date; items: Item[] }> = [];
    for (const it of items) {
      const g = groups.find((x) => isSameDay(x.day, it.at));
      if (g) g.items.push(it); else groups.push({ label: dayLabel(it.at), day: it.at, items: [it] });
    }

    const now = new Date();
    const followUps = (data?.meetings ?? []).flatMap((m) =>
      (m.ai_action_items ?? []).map((raw, i) => ({
        key: `${m.id}-${i}`, ...parseActionItem(raw), meeting: m.title ?? 'Meeting',
      })),
    ).filter((f) => f.action).sort((a, b) => (a.due?.getTime() ?? Infinity) - (b.due?.getTime() ?? Infinity));
    const overdue = followUps.filter((f) => f.due && isBefore(f.due, now)).length;

    const emails = data?.emails ?? [];
    const needsReply = emails.filter((e) => e.escalation_flag && e.is_read === false);
    const perProject = new Map<string, { unread: number; escalated: number }>();
    for (const e of emails) {
      if (!e.project_id || !projectName.get(e.project_id)) continue;
      const row = perProject.get(e.project_id) ?? { unread: 0, escalated: 0 };
      if (e.is_read === false) row.unread++;
      if (e.escalation_flag) row.escalated++;
      perProject.set(e.project_id, row);
    }
    return {
      groups, followUps, overdue, needsReply,
      unread: emails.filter((e) => e.is_read === false).length,
      escalated: emails.filter((e) => e.escalation_flag).length,
      meetingCount: (data?.meetings ?? []).length,
      byProject: [...perProject.entries()].map(([id, c]) => ({ id, name: projectName.get(id) as string, ...c })).sort((a, b) => b.escalated - a.escalated || b.unread - a.unread),
    };
  }, [data]);

  if (isLoading) return <div className="space-y-4" aria-busy="true"><Skeleton className="h-20 w-full" /><Skeleton className="h-72 w-full" /></div>;
  if (isError) return <p role="alert" className="text-sm text-muted-foreground">{t('Communication could not be loaded. Try again shortly.')}</p>;
  const googlePrompt = mailEmpty && googleStatus && !googleStatus.connected
    ? <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={connectGoogle}>{t('Connect Google to see your project mail and meetings')}</Button>
    : null;

  if (view.groups.length === 0) {
    return (
      <div className="space-y-3 rounded-lg border border-dashed border-border p-6">
        <p className="text-sm text-muted-foreground">{t('No messages or meetings in the last')} {DAYS}  {t('days. Connect Microsoft 365 or Teams under Integrations and they will appear here.')}</p>
        {googlePrompt}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {googlePrompt}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {canEmail && <Stat label={t('Unread')} value={view.unread} />}
        {canEmail && <Stat label={t('Escalated')} value={view.escalated} tone="alert" />}
        {canMeetings && <Stat label={t('Meetings ({0}d)', { 0: DAYS })} value={view.meetingCount} />}
        {canMeetings && <Stat label={t('Overdue follow-ups')} value={view.overdue} tone="alert" />}
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Panel title={t('Timeline · last {0} days', { 0: DAYS })} icon={<Inbox className="h-5 w-5" />}>
          <div className="space-y-4">
            {view.groups.map((g) => (
              <div key={g.label + g.day.toDateString()}>
                <h3 className="mb-1 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{tx(g.label)}</h3>
                <ul>
                  {g.items.map((it) => (
                    <li key={`${it.kind}-${it.id}`}>
                      <button type="button" onClick={() => onNavigate(it.kind === 'email' ? 'inbox' : 'meetings')} className="flex w-full items-start gap-3 rounded-md px-2 py-2.5 text-start hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                        {it.kind === 'meeting'
                          ? <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-label={t('Meeting')} />
                          : it.escalated
                            ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-label={t('Escalated email')} />
                            : it.unread
                              ? <Mail className="mt-0.5 h-4 w-4 shrink-0 text-foreground" aria-label={t('Unread email')} />
                              : <MailOpen className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-label={t('Read email')} />}
                        <span className="min-w-0 flex-1">
                          <span className={cn('block truncate text-sm', it.kind === 'email' && it.unread ? 'font-bold' : 'font-semibold')}>{tx(it.title)}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {it.sub}{it.kind === 'meeting' && it.actions ? t(' · {0} action item{1}', { 0: it.actions, 1: it.actions === 1 ? '' : 's' }) : ''}
                          </span>
                        </span>
                        <span className="flex shrink-0 flex-col items-end gap-1">
                          <span className="font-mono text-[11px] text-muted-foreground">{ago(it.at)}</span>
                          {it.kind === 'email' && it.project && <span className="max-w-[10rem] truncate rounded-full bg-secondary px-2 py-0.5 text-[11px] text-secondary-foreground">{it.project}</span>}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Panel>

        <div className="space-y-5">
          {canMeetings && (
            <Panel title={t('Follow-ups from meetings')} icon={<CheckSquare className="h-5 w-5" />} action={t('Meetings')} onAction={() => onNavigate('meetings')}>
              {view.followUps.length === 0 ? <p className="text-sm text-muted-foreground">{t('No open action items.')}</p> : (
                <ul className="space-y-2">
                  {view.followUps.slice(0, 6).map((f) => {
                    const late = f.due && isBefore(f.due, new Date());
                    return (
                      <li key={f.key} className="rounded-md border border-border px-3 py-2">
                        <p className="text-sm font-semibold">{f.action}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {f.owner} · {f.meeting}
                          {f.due && <span className={cn('ms-1', late && 'font-semibold text-destructive')}>· {late ? t('overdue, was due') : t('due')} {format(f.due, 'd MMM')}</span>}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Panel>
          )}

          {canEmail && (
            <Panel title={t('Needs a reply')} icon={<AlertTriangle className="h-5 w-5" />} action={t('Inbox')} onAction={() => onNavigate('inbox')}>
              {view.needsReply.length === 0 ? <p className="text-sm text-muted-foreground">{t('No unread escalations.')}</p> : (
                <ul className="space-y-1">
                  {view.needsReply.slice(0, 5).map((e) => (
                    <li key={e.id} className="text-sm"><span className="font-semibold">{e.subject ?? t('(no subject)')}</span> <span className="text-xs text-muted-foreground">· {e.sender_name}</span></li>
                  ))}
                </ul>
              )}
            </Panel>
          )}

          {canEmail && view.byProject.length > 0 && (
            <Panel title={t('By project')} icon={<Mail className="h-5 w-5" />}>
              <ul className="space-y-1.5">
                {view.byProject.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 text-sm">
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">{p.unread}  {t('unread')}</span>
                    {p.escalated > 0 && <span className="rounded-full bg-destructive/10 px-2 py-0.5 font-mono text-[11px] text-destructive">{p.escalated}  {t('escalated')}</span>}
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunicationHub;
